import { useCallback, useEffect, useMemo, useState } from "react";
import { getLastClosingDate } from "@/api/daily-stock-api";
import {
  parseToDate,
  daysBetween,
  toLocalDateOnly,
} from "./stock-closing-utils";

export function useStockClosing(lastClosingDate?: string | Date | null) {
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastClosedAt, setLastClosedAt] = useState<Date | null>(null);
  const [missingStockVariantsCount, setMissingStockVariantsCount] = useState(0);

  const today = useMemo(() => toLocalDateOnly(new Date()), []);

  const hydrateFromProp = useCallback((v?: string | Date | null) => {
    if (!v) return;
    const d = parseToDate(v);
    if (d) setLastClosedAt(d);
  }, []);

  const load = useCallback(async () => {
    setFetching(true);
    setError(null);
    try {
      const res = await getLastClosingDate();
      if (!res.success) {
        setError(res.message ?? "Failed to load last closing date");
        return;
      }
      if (res.data.lastClosingDate != null) {
        const parsed = parseToDate(res.data.lastClosingDate);
        if (!parsed) throw new Error("Invalid date returned by API");
        setLastClosedAt(parsed);
      } else {
        setLastClosedAt(null);
      }
      setMissingStockVariantsCount(res.data.missingStockVariantsCount ?? 0);
    } catch (e: any) {
      setError(e?.message ?? "Failed to load last closing date");
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    hydrateFromProp(lastClosingDate);
    load();
  }, []);

  useEffect(() => {
    hydrateFromProp(lastClosingDate);
  }, [hydrateFromProp, lastClosingDate]);

  const needsClosing = useMemo(() => {
    // Need closing if there are missing days OR missing variants
    if (missingStockVariantsCount > 0) return true;
    if (!lastClosedAt) return false;
    return daysBetween(lastClosedAt, today) >= 1;
  }, [lastClosedAt, today, missingStockVariantsCount]);

  const missingDays = useMemo(() => {
    if (!lastClosedAt) return 0;
    return Math.max(0, daysBetween(lastClosedAt, today));
  }, [lastClosedAt, today]);

  return {
    lastClosedAt,
    today,
    fetching,
    error,
    needsClosing,
    missingDays,
    missingStockVariantsCount,
    load,
    setError,
  };
}
